/**
 * The `AlgoliaCustomHooks` class is designed to manage and customize specific behaviors of
 * Algolia's Shopify integration through the use of hooks, events, and dynamic DOM manipulation.
 * It initializes custom hooks, binds events, and provides methods for customizing filters, templates,
 * and facet options.
 */
class AlgoliaCustomHooks {

    constructor() {
        let categoryTarget = null;
        this.firstRun = true;
        this.indexName = window.algolia?.config?.index_prefix ?? '' + 'products';
    }

    /**
     * Initializes the component by setting up event listeners and applying initial behaviors for specific elements
     * based on screen size and user interactions. This method wires up behaviors for displaying and hiding
     * UI elements in response to certain events and conditions.
     *
     * @return {void} This method does not return a value.
     */
    init() {
        let _this = this;
        this.initEnrichmentObserver();
        this.initHitsObserver();

        document.addEventListener('algolia.hooks.initialize', () => {
            _this.initHooks();

            if (screen.width < 768) {
                setTimeout(function () {
                    document.querySelector('.ais-facets').classList.add('ais-facets__shown');
                    document.querySelectorAll('.ais-facet-dropdown-wrapper').forEach(function (el) {
                        el.classList.add('open');
                        _this.setInitialHeight(el);
                    });

                    setTimeout(function () {
                        document.querySelectorAll('.ais-facet-dropdown-wrapper').forEach(function (el) {
                            el.classList.remove('open');
                        });
                        document.querySelector('.ais-facets').classList.remove('ais-facets__shown');
                    }, 250);
                }, 250);
            }
            document.querySelectorAll('.ais-facet-price label').forEach(function (el) {
                el.classList.add('open');
            });

            // reset styles
            _this.resetStyles();
        });

        if (window.search) {
            window.search.on('render', () => {
                _this.updateProductCardTitles();
                _this.addPromoBlocks();
            });
        } else {
            document.addEventListener('DOMContentLoaded', () => {
                setTimeout(() => {
                    if (window.search) {
                        window.search.on('render', () => {
                            _this.updateProductCardTitles();
                            _this.addPromoBlocks();
                        });
                    }
                }, 1000);
            });
        }

    }

    /**
     * Binds event listeners to elements with a specific class name within the DOM.
     *
     * The method adds click event listeners to header elements within a dropdown wrapper.
     * It ensures an initial height is set for each filter dropdown if not already defined.
     * When a header element is clicked, it prevents default propagation behaviors and
     * triggers an animation for the corresponding filter.
     *
     * @return {void} This method does not return any value.
     */
    bindEvents() {
        let _this = this;

        document.querySelectorAll('.ais-facet-dropdown-wrapper .ais-facet--header').forEach(function (el) {


            let filter = el.closest('.ais-facet-dropdown-wrapper');
            if (typeof filter.dataset.height == 'undefined') {
                _this.setInitialHeight(filter);
            }

            el.addEventListener('click', function (e) {
                e.stopImmediatePropagation();
                e.stopPropagation();
                _this.animateFilter(el);
            });
        });
    }

    /**
     * Sets the initial height of a specified element based on its child elements.
     *
     * @param {HTMLElement} el - The parent element containing the necessary child elements to calculate height.
     * @return {void} This method does not return a value.
     */
    setInitialHeight(el) {
        let filter = el.querySelector('.ais-facet-dropdown-container');
        let heightEl = filter.querySelector('scroll-shadow');
        if (!heightEl) heightEl = filter.querySelector('.ais-Panel-body');
        let height = heightEl.offsetHeight;
        if (height > 0) {
            filter.dataset.height = height;
            filter.style.height = filter.dataset.height + 'px';
        }
    }

    /**
     * Initializes and registers custom hooks for modifying the behavior of AlgoliaShopify functionalities.
     *
     * The method registers various hooks for tasks such as:
     * - Adjusting options for facet panel and facet parameters
     * - Modifying templates and filtering strings
     * - Customizing search and filtering logic
     *
     * The registered hooks include:
     * - `beforeInstantSearchFacetPanelOptions`
     * - `beforeInstantSearchStatsTemplate`
     * - `beforeInstantSearchFacetParamsOptions`
     * - `beforeAutocompleteFiltersString`
     * - `beforeInstantSearchFiltersString`
     *
     * @return {void} Does not return a value.
     */
    initHooks() {

        const _this = this;

        algoliaShopify.hooks.registerHook('beforeInstantSearchFacetPanelOptions', (params, facet) => {
            return this.beforeInstantSearchFacetPanelOptions(params, facet)
        });

        // Filter search bar for allowed collections
        algoliaShopify.hooks.registerHook('beforeInstantSearchStatsTemplate', (_defaultTemplate, data, html) => {
            return this.beforeInstantSearchStatsTemplate(_defaultTemplate, data, html)
        });

        algoliaShopify.hooks.registerHook('beforeInstantSearchFacetParamsOptions', (params, facet) => {
            return this.beforeInstantSearchFacetParamsOptions(params, facet)
        });

        algoliaShopify.hooks.registerHook('beforeAutocompleteFiltersString', (_defaultFilter) => {
            return this.beforeAutocompleteFiltersString(_defaultFilter)
        });

        // Filter PLPs and search page for allowed collections
        algoliaShopify.hooks.registerHook('beforeInstantSearchFiltersString', (_defaultFilter) => {
            return this.beforeInstantSearchFiltersString(_defaultFilter)
        });

        algoliaShopify.hooks.registerHook('beforeInstantSearchProductTemplate', (_defaultTemplate, hit, html, components) => {
            return this.beforeInstantSearchProductTemplate(_defaultTemplate, hit, html, components)
        });

        // Remove default widgets
        algoliaShopify.hooks.registerHook('afterISStartRemoveDefaultWidget', function(defaultWidgets) {
            // Find and return the default pagination widget
            const defaultPaginationWidget = defaultWidgets.find(
                widget => widget.$$widgetType === 'ais.pagination'
            );

            const defaultHitsWidget = defaultWidgets.find(
                widget => widget.$$widgetType === 'ais.hits'
            );

            return [defaultPaginationWidget, defaultHitsWidget];
        });

        // Add infinite hits widget
        algoliaShopify.hooks.registerHook('beforeISStartAddWidgetArray', function() {

            const { infiniteHits } = window.instantsearch.widgets;

            const infiniteHitsWidget = infiniteHits({
                container: '.ais-hits-container',
                cssClasses: {
                    list: 'products collection row small-up-2 medium-up-4',
                    item: 'column'
                },
                templates: {
                    item(hit, { html, components }) {
                        return _this.beforeInstantSearchProductTemplate(null, hit, html, components);
                    }
                }
            });

            return [infiniteHitsWidget];
        });
    }

    /**
     * Modifies the InstantSearch facet panel options based on specific conditions, such as the facet name or title.
     *
     * @param {Object} params - The initial parameters for the facet panel.
     * @param {Object} facet - The facet object containing metadata such as name and title.
     * @return {Object} Modified parameters for the facet panel, including additional CSS classes and templates if applicable.
     */
    beforeInstantSearchFacetPanelOptions(params, facet) {
        // Add the price subheader
        if (facet.name == "price" || facet.title == "Price") {
            return {
                ...params,
                cssClasses: {
                    label: 'active'
                },
                templates: {
                    header(options, {html}) {
                        if (options.results) {
                            return html`<span
                                    class="ais-facet--subheader">The highest price is $${options.range.max}</span>`;
                        }
                    },
                }
            }
        }

        return {
            ...params
        }
    }

    /**
     * Processes and modifies the instant search stats template before rendering.
     *
     * @param {string} _defaultTemplate - The default template for displaying search statistics.
     * @param {Object} data - The data related to the search results, including result counts and flags indicating the presence of results.
     * @param {Function} html - A template literal tag function used for generating HTML content dynamically.
     * @return {string} The modified HTML template with updated search stats information.
     */
    beforeInstantSearchStatsTemplate(_defaultTemplate, data, html) {
        let _this = this;
        _this.moveSelectedFiltersToHeader();
        _this.updateCurrentRefinements();

        if(document.querySelector('body').classList.contains('template-search')) {
            this.updateSearchPageNumberOfResults(data);
        }

        document.querySelectorAll('.ais-facets .ais-facet-dropdown-wrapper').forEach(function (el) {
            _this.addScrollShadow(el);
            _this.addExpandIcon(el);
            _this.closeFiltersFirstTime(el);
            if (!_this.firstRun) {
                _this.fixFilterHeights(el);
            }
        });

        // update current refinements when filters change
        if (_this.firstRun) {
            const target = document.querySelector('.ais-current-refined-values-container .ais-Panel-body > div');
            const observer = new MutationObserver(function (mutations) {
                mutations.forEach(function (mutation) {
                    _this.updateCurrentRefinements();
                });
            });
            const config = {
                childList: true
            };
            observer.observe(target, config);

            // The hits observer is now handled in initHitsObserver()
        }

        _this.updateProductCardTitles();

        _this.bindEvents();
        _this.firstRun = false;

        setTimeout(() => {
            _this.observeProductCards();
            _this.initInfiniteScrollObserver();
        }, 100);


        let numberOfHits = data.hitsPerPage * (data.page + 1);
        if(numberOfHits > data.nbHits) numberOfHits = data.nbHits;

        return html`
            ${data.hasOneResult &&
            html`
                <span class="ais-stats--nb-results">
                1 result found
              </span>
            `}
            ${data.hasManyResults &&
            html`
                ${numberOfHits}
                ${' '}out of
                <span class="ais-stats--nb-results">
                ${' '} <span class="number-of-results">${data.nbHits ?? ''}</span> ${' '}
                    products
              </span>
            `}
        `;
    }

    initEnrichmentObserver() {
        this.enrichmentObserver = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    this.fetchProductData(entry.target);
                    this.enrichmentObserver.unobserve(entry.target);
                }
            });
        }, { rootMargin: '200px' });
    }

    observeProductCards() {
        const cards = document.querySelectorAll('.alg-product-card:not(.observed)');
        cards.forEach(card => {
            this.enrichmentObserver.observe(card);
        });
    }

    fetchProductData(card) {
        const handle = card.dataset.handle;
        if (!handle || card.classList.contains('observed')) return;

        card.classList.add('observed');

        fetch(`/products/${handle}?view=data`)
            .then(response => response.text())
            .then(html => {
                const parser = new DOMParser();
                const doc = parser.parseFromString(html, 'text/html');
                
                const mediaHtml = doc.querySelector('[data-product-media-html] a')?.innerHTML;
                const sizesHtml = doc.querySelector('[data-product-sizes-html] .product-card-sizes--container')?.innerHTML;

                if (mediaHtml) {
                    const mediaContainer = card.querySelector('.product-featured-image-link');
                    if (mediaContainer) {
                        mediaContainer.innerHTML = mediaHtml;
                    }
                }

                if (sizesHtml) {
                    const sizesContainer = card.querySelector('.product-card-sizes--container');
                    if (sizesContainer) {
                        sizesContainer.innerHTML = sizesHtml;
                    }
                }

                if (typeof card.init === 'function') {
                    card.init();
                }
            })
            .catch(err => console.error('Error fetching product data:', err));
    }

    /**
     * Modifies the given InstantSearch facet parameter options based on specific facet names
     * to customize their templates for rendering in the UI.
     *
     * @param {Object} params - The original parameters for the InstantSearch facet.
     * @param {Object} facet - Information about the current facet, including its name.
     * @return {Object} The modified parameters containing custom templates when applicable.
     */
    beforeInstantSearchFacetParamsOptions(params, facet) {
        console.log('params3',params)
        console.log('facet3',facet)

        let metaobjects = {
            'safety_features': 'Safety features',
            'work_boot_features': 'Work Boot Features',
            'safety_toe_type': 'Safety Toe Type',
            'toe_shape': 'Western Boot Toe Shape',
            'heel_type': 'Western Boot Heel Type',
            'toe_type': 'Safety Toe Type'
        }

        if (facet.name == "options.color") {
            return {
                ...params,
                templates: {
                    item(options, { html }) {
                        return html`<label class="ais-RefinementList-label ais-facet--label label-color--${options.label}">
                            <input type="checkbox" class="ais-RefinementList-checkbox ais-facet--checkbox ${options.isRefined ? 'checked' : ''}" ${options.isRefined ? 'checked' : ''} onload="" />
                            ${options.label.replace('-', ' ')}
                        </label>
                        `
                    },
                },
            }
        }

        if (facet.name == "color_ids") {
            return {
                ...params,
                templates: {
                    item(options, {html}) {
                        let metafieldIDs = options.value;

                        let colorName = '';

                        metafieldIDs.split(',').forEach(function (metafieldID) {
                            for (const [key, value] of Object.entries(window.metaobjectFilters)) {
                                if (key === 'Colors' && typeof value[metafieldID] != 'undefined') {
                                    let color = value[metafieldID].split('---');
                                    colorName = color[1];
                                }
                            }
                        })

                        return html`<label
                                class="ais-RefinementList-label ais-facet--label color-filter label-color--${colorName}">
                            <input value='${options.value}' type="checkbox"
                                   class="ais-RefinementList-checkbox ais-facet--checkbox ${options.isRefined ? 'checked' : ''}"
                                   ${options.isRefined ? 'checked' : ''} onload=""/>
                            ${colorName.replace('-', ' ')}
                        </label>
                        `
                    },
                },
            }
        }

        if (metaobjects.hasOwnProperty(facet.name)) {
            return {
                ...params,
                templates: {
                    item(options, {html}) {
                        let metafieldIDs = options.value;

                        let featureImg = '';
                        let featureName = '';

                        metafieldIDs.split(',').forEach(function (metafieldID) {

                            for (const [key, value] of Object.entries(window.metaobjectFilters)) {
                                if (key === metaobjects[facet.name] && typeof value[metafieldID] != 'undefined') {
                                    let feature = value[metafieldID].split('|||');
                                    featureImg = feature[0];
                                    featureName = feature[1];
                                }
                            }
                        })

                        return html`<label class="ais-RefinementList-label ais-facet--label safety-feature-filter">
                            <input value='${options.value}' type="checkbox"
                                   class="ais-RefinementList-checkbox ais-facet--checkbox ${options.isRefined ? 'checked' : ''}"
                                   ${options.isRefined ? 'checked' : ''} onload=""/>
                            <img src="${featureImg}" alt=""/>
                            <span>${featureName}</span>
                        </label>
                        `
                    },
                },
            }
        }

        if (facet.name == "collections") {
            return {
                ...params,
                templates: {
                    // Set the checked state of the checkbox if necessary, for some reason it's not working if done in the template
                    item(options, {html}) {
                        let uniqueId = Math.random().toString(16).slice(2);
                        return html`<label class="ais-RefinementList-label ais-facet--label">
                            <input type="checkbox"
                                   class="ais-RefinementList-checkbox ais-facet--checkbox id--${uniqueId} ${options.isRefined ? 'checked' : ''}"
                                   ${options.isRefined ? 'checked' : ''} onload=""/>
                            ${options.label.replace('-', ' ')}</label>

                        <script>
                            categoryTarget = document.querySelector('.ais-RefinementList-checkbox.id--${uniqueId}');
                            if (categoryTarget && categoryTarget.classList.contains('checked')) categoryTarget.setAttribute('checked', 'checked');
                        </script>`
                    },
                },
            }
        }

        return {
            ...params
        }
    }

    /**
     * Constructs and returns a filter string to be used for autocomplete queries,
     * based on the stock policy and allowed collections configuration.
     *
     * @param {string} _defaultFilter - The default filter string to be used if no specific conditions are met.
     * @return {string} The constructed filter string, incorporating stock policy and allowed collections.
     */
    beforeAutocompleteFiltersString(_defaultFilter) {
        // Filters for stock policy
        let stockPolicyFilter = '';

        if (algoliaShopify.config.stock_policy === 'deny') {
            // For 'deny' we will filter out all items based on inventory quantity
            stockPolicyFilter = 'inventory_quantity > 0';
        } else if (algoliaShopify.config.stock_policy === 'continue') {
            /**
             * For 'continue' we will filter on `inventory_available` attribute whose
             * value is dependent on:
             * `inventory_quantity > 0 OR inventory_policy == 'continue'`
             */
            stockPolicyFilter = 'inventory_available:true';
        }

        let filters = '';
        // Only filter by collections if user is logged in with a store
        if (window.store) {
            // User is logged in with a store
            if (window.allowed_collections && window.allowed_collections.length > 0) {
                // Has allowed collections - filter by them
                filters = (stockPolicyFilter != '' ? `${stockPolicyFilter}  AND ` : '')
                    + '(' + window.allowed_collections.map(c => `collection_ids: "${c}"`).join(' OR ') + ')';
            } else {
                // Store exists but no allowed collections - filter out all products
                filters = (stockPolicyFilter != '' ? `${stockPolicyFilter}  AND ` : '')
                    + 'collections: "non-existing-collection"';
            }
        } else {
            // User is logged out (no store) - don't filter by collections, show all products
            filters = stockPolicyFilter;
        }

        return filters;
    }

    /**
     * Constructs a filter string to be used for instant search based on stock policy, default filters, and allowed collections.
     *
     * @param {string} _defaultFilter - The default filter string to be combined with the constructed stock policy and collection filters.
     * @return {string} The resulting filter string, which combines the default filter with additional filters based on stock policy and allowed collections.
     */
    beforeInstantSearchFiltersString(_defaultFilter) {
        // Filters for stock policy
        let stockPolicyFilter = '';

        if (algoliaShopify.config.stock_policy === 'deny') {
            // For 'deny' we will filter out all items based on inventory quantity
            stockPolicyFilter = 'inventory_quantity > 0';
        } else if (algoliaShopify.config.stock_policy === 'continue') {
            /**
             * For 'continue' we will filter on `inventory_available` attribute whose
             * value is dependent on:
             * `inventory_quantity > 0 OR inventory_policy == 'continue'`
             */
            stockPolicyFilter = 'inventory_available:true';
        }

        let filters = '';
        // Only filter by collections if user is logged in with a store
        if (window.store) {
            // User is logged in with a store
            if (window.allowed_collections && window.allowed_collections.length > 0) {
                // Has allowed collections - filter by them
                filters = (stockPolicyFilter != '' ? `${stockPolicyFilter}  AND ` : '')
                    + '(' + window.allowed_collections.map(c => `collection_ids: "${c}"`).join(' OR ') + ')';
            } else {
                // Store exists but no allowed collections - filter out all products
                filters = (stockPolicyFilter != '' ? `${stockPolicyFilter}  AND ` : '')
                    + 'collections: "non-existing-collection"';
            }
        } else {
            // User is logged out (no store) - don't filter by collections, show all products
            filters = stockPolicyFilter;
        }

        filters = _defaultFilter + (_defaultFilter.length > 0 ? ' AND ' : '') + filters;

        return filters;
    }

    /**
     * Toggles the visibility and animated height adjustment of a filter dropdown element
     * associated with the provided label.
     *
     * @param {Element} label - The label element used to identify and toggle the associated filter dropdown.
     * @return {void} This method does not return a value.
     */
    animateFilter(label) {
        let filter = label.closest('.ais-facet-dropdown-wrapper').querySelector('.ais-facet-dropdown-container');
        if (typeof filter.dataset.height == 'undefined') {
            let heightEl = filter.querySelector('scroll-shadow');
            if (!heightEl) heightEl = filter.querySelector('.ais-Panel-body');
            filter.dataset.height = heightEl.offsetHeight;
        }

        if (filter.classList.contains('closed')) {
            filter.classList.remove('closed');
            filter.style.maxHeight = filter.dataset.height + 'px';
            label.classList.add('open');
        } else {
            filter.classList.add('closed');
            filter.style.maxHeight = '0px';
            label.classList.remove('open');
        }
    }

    /**
     * Adds a scroll shadow element to the specified container element.
     * The method replaces the existing panel element within the container
     * with a new scroll shadow element while preserving its contents.
     * If the container is part of certain excluded element types, the method does nothing.
     *
     * @param {Element} el The container element to which the scroll shadow should be added.
     *                      This element is expected to contain a child with the class `ais-Panel`.
     * @return {void} Does not return a value.
     */
    addScrollShadow(el) {
        if (el.closest('.ais-facet-price') || el.closest('.ais-facet-inventory_available')) return;
        // Add scroll-shadow - create a new el and replace the panel el with it, move the contents to keep events
        let panel = el.querySelector('.ais-Panel')
        if (panel && !el.querySelector('scroll-shadow')) {
            var shadow = document.createElement('scroll-shadow');
            shadow.classList.add('ais-panel');
            let parent = panel.parentNode;
            shadow.appendChild(panel.querySelector('.ais-Panel-body'));
            panel.remove();
            parent.appendChild(shadow);
        }
    }

    /**
     * Adds an expand icon to the header element within the specified container.
     *
     * @param {Element} el - The DOM element that serves as the container. It should contain a child element with the class 'ais-facet--header'.
     * @return {void} This method does not return a value.
     */
    addExpandIcon(el) {
        let header = el.querySelector('.ais-facet--header');
        if (header && !header.querySelector('.expand-icon')) {
            let expandIcon = document.createElement('span');
            expandIcon.classList.add('expand-icon');
            header.prepend(expandIcon);
        }
    }

    /**
     * Closes all but the first filter dropdown within a specified filter container if they are not already closed.
     *
     * @param {HTMLElement} el The DOM element from which the method will locate the closest filter container and process its filter dropdowns.
     * @return {void} Does not return a value.
     */
    closeFiltersFirstTime(el) {
        let facets = el.closest('.ais-facets');
        if (facets && !facets.classList.contains('filters-closed')) {
            facets.querySelectorAll('.ais-facet-dropdown-wrapper:not(:first-child)').forEach(function (el) {
                let target = el.querySelector('.ais-facet-dropdown-container');
                target.classList.add('closed');

                let heightEl = target.querySelector('scroll-shadow');
                if (!heightEl) heightEl = target.querySelector('.ais-Panel-body');
                target.dataset.height = heightEl.offsetHeight;
                target.dataset.initialHeight = target.dataset.height;
                target.style.maxHeight = '0px';
            });

            facets.classList.add('filters-closed');
        }
    }

    /**
     * Moves selected filter elements and relevant components from their original positions
     * to a designated header section in the UI. This creates a cleaner, structured layout
     * for the search interface by grouping filter-related components together in the header.
     *
     * @return {void} This method does not return any value.
     */
    moveSelectedFiltersToHeader() {
        let el = document.querySelector('.ais-facets .ais-current-refined-values-container');
        let el2 = document.querySelector('.ais-facets .ais-clear-refinements-container');
        let target = document.querySelector('.ais-block .ais-search-header');
        if (target.querySelector('.left')) return;

        let newDiv = document.createElement('div');
        newDiv.classList.add('left');
        newDiv.prepend(el2);
        newDiv.prepend(el);
        target.prepend(newDiv);

        let statsEl = document.querySelector('.ais-block .ais-search-header .ais-stats-container');
        let displayEl = document.querySelector('.ais-block .ais-search-header .ais-change-display');
        let sortEl = document.querySelector('.ais-block .ais-search-header .ais-sort');
        let newDiv2 = document.createElement('div');
        newDiv2.classList.add('right');
        newDiv2.prepend(sortEl);
        newDiv2.prepend(displayEl);
        newDiv2.prepend(statsEl);
        target.appendChild(newDiv2);
    }

    /**
     * Adjusts the height of the filter dropdown to ensure it fits its content dynamically.
     *
     * @param {HTMLElement} el - The parent element containing the filter dropdown that needs height adjustment.
     * @return {void} This method does not return a value.
     */
    fixFilterHeights(el) {
        let filter = el.querySelector('.ais-facet-dropdown-container');
        if (filter.classList.contains('closed')) return;

        let heightEl = filter.querySelector('scroll-shadow');
        if (!heightEl) heightEl = filter.querySelector('.ais-Panel-body');
        filter.style.maxHeight = 'unset';
        filter.dataset.height = heightEl.offsetHeight > 100 ? heightEl.offsetHeight : filter.dataset.initialHeight;
        filter.style.maxHeight = filter.dataset.height + 'px';
    }

    /**
     * Updates the current refinements displayed on the page.
     * This method iterates through elements with the CSS class `ais-current-refined-values--link`, modifies their content based on the associated dataset value,
     * and updates the targeted display dynamically, including handling specific cases for filters with associated meta-data.
     *
     * Handles the following scenarios:
     * - Displays the raw value when the dataset value is not a number.
     * - Displays filter information with associated images or colors when applicable.
     * - Formats the content dynamically based on the structure of the filter data.
     *
     * @return {void} This method does not return a value. It updates the DOM elements directly.
     */
    updateCurrentRefinements() {
        let _this = this;

        document.querySelectorAll('.ais-current-refined-values--link').forEach(function (el) {
            let target = el.querySelector('div');
            let value = el.dataset.value;
            let filter = _this.findSelectedFilterData(value);
            if (isNaN(parseInt(value))) {
                target.innerText = value;
            } else if (filter) {
                if (filter.indexOf('|||') > -1) {
                    let filterArray = filter.split('|||');
                    let filterImg = filterArray[0];
                    let imgEl = document.createElement('img');
                    imgEl.src = filterImg;
                    target.innerHTML = imgEl.outerHTML + ' ' + filterArray[1];
                } else if (filter.indexOf('Colors--') > -1) {
                    let filterArray = filter.split('Colors--')[1].split('---');
                    let color = filterArray[0];
                    let name = filterArray[1];
                    target.innerHTML = `<span class="color-filter" style="background-color:${color};"></span>${name}`;
                } else {
                    target.innerHTML = filter;
                }
            }
        });
    }

    /**
     * Finds the selected filter data for a given metaobject ID from the global `window.metaobjectFilters`.
     *
     * @param {string} metaobjectID - The ID of the metaobject to find in the filters.
     * @return {string|null} The selected filter data for the given metaobject ID, or null if no match is found.
     */
    findSelectedFilterData(metaobjectID) {
        for (const [key, value] of Object.entries(window.metaobjectFilters)) {
            if (key === 'Colors' && value.hasOwnProperty(metaobjectID)) {
                return key + '--' + value[metaobjectID];
            } else if (value.hasOwnProperty(metaobjectID)) return value[metaobjectID];
        }
        return null;
    }


    /**
     * Updates the search page to display the number of results found for a given query.
     *
     * @param {Object} data An object containing the search query and number of hits.
     * @param {number} data.nbhits The number of search results returned.
     * @param {string} data.query The search query string used to find results.
     * @return {void} This method does not return a value.
     */
    updateSearchPageNumberOfResults(data) {
        let titleTarget = document.querySelector('title');
        let length = data.nbHits;
        let resultsString = length > 1 ? 'results' : 'result';

        titleTarget.innerText = `Search: ${length} ${resultsString} found for "${data.query}" – Circle A Western Wear`;

        let resultsTarget = document.querySelector('.ais-stats-container .ais-stats--nb-results .number-of-results');
        if(resultsTarget) {
            resultsTarget.innerText = length;
        }
    }

    /**
     * Stores clean titles from hit data for later DOM manipulation.
     *
     * @param {Object} template - The virtual DOM template element.
     * @param {Object} hit - The hit/product data object.
     * @return {Object} The template unchanged.
     */
    beforeInstantSearchProductTemplate(template, hit, html, components) {
        if (!hit) {
            return template;
        }

        if (!window.algoliaCleanTitles) {
            window.algoliaCleanTitles = {};
        }

        const cleanTitle = hit.meta?.custom?.clean_title;
        
        if (cleanTitle && hit.handle) {
            window.algoliaCleanTitles[hit.handle] = cleanTitle;
        }

        const variantStrings = window.theme?.variantStrings || {};

        let buttonContent = variantStrings.addToCart || 'Add to cart';
        let buttonTag = 'button';

        if (!hit.inventory_available) {
            buttonContent = variantStrings.soldOut || 'Sold out';
        } else if (hit.meta?.theme?.preorder) {
            buttonContent = variantStrings.preOrder || 'Pre-order';
        } 

        const primaryImage = algoliaShopify.helpers.mediumImage(hit);
        const secondaryImage = hit.images && hit.images.length > 1 ? hit.images[1] : null;

        return html`
            <product-card class="product-card text-left uppercase-font-true alg-product-card"
                data-algolia-index="${hit.index}"
                data-algolia-position="${hit.productPosition}"  
                data-algolia-queryid="${hit.queryID}"
                data-algolia-objectid="${hit.objectID}"
                data-handle="${hit.handle}"
                data-variant-id="${hit.objectID}"
                data-product-id="${hit.id}"
                onClick="${() => algoliaShopify.helpers.handleItemClick(hit)}"
            >
                <figure class="product-featured-image ${secondaryImage ? 'thb-hover' : ''}">
                    <!-- Badges -->
                    <span class="product-card--badges top right">
                        ${!hit.inventory_available ? html`<span class="badge out-of-stock">${variantStrings.soldOut || 'Sold out'}</span>` : ''}
                        ${hit.compare_at_price > hit.price && hit.inventory_available ? html`<span class="badge onsale">Save ${Math.round((hit.compare_at_price - hit.price) * 100 / hit.compare_at_price)}%</span>` : ''}
                        ${hit.meta?.theme?.preorder && hit.inventory_available ? html`<span class="badge pre-order">${variantStrings.preOrder || 'Pre-order'}</span>` : ''}
                    </span>
                    <a href="${algoliaShopify.helpers.instantsearchLink(hit)}" title="${hit.title}" class="product-featured-image-link aspect-ratio aspect-ratio--portrait" style="--padding-bottom: 125%;">
                        ${secondaryImage ? html`<img class="product-secondary-image lazyload" src="data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==" data-src="${secondaryImage}" alt="${hit.title}" loading="lazy" />` : ''}
                        <img class="product-primary-image lazyload" src="${primaryImage}" data-src="${primaryImage}" alt="${hit.title}" loading="lazy" />
                    </a>
                    <div class="product-card-sizes--container no-js-hidden">
                    </div>
                </figure>
                <div class="product-card-info">
                    <div class="product-card-vendor">
                        <a href="/collections/vendors?q=${hit.vendor}" title="${hit.vendor}">${hit.vendor}</a>
                    </div>
                    <a href="${algoliaShopify.helpers.instantsearchLink(hit)}" title="${hit.title}" class="product-card-title">
                        ${hit.title}
                    </a>
                    <span class="price">
                        <ins data-wpd-product-handle="${hit.handle}" data-wpd-variant-id="${hit.objectID}" data-wpd-variant-price="${hit.price*100}" data-wpd-variant-compare-at-price="${hit.compare_at_price*100}" data-wpd-product-collection-ids="${hit.collection_ids}" data-wpd-product-id="${hit.id}">
                            <span class="card-price-amount amount ${hit.compare_at_price > hit.price ? 'discounted' : ''}">${algoliaShopify.helpers.displayPrice(hit, hit._distinct)}</span>
                        </ins>
                    </span>
                </div>
            </product-card>
        `;
    }

    /**
     * Initializes an IntersectionObserver to trigger the "Load More" button 
     * when it is within 200px of the viewport.
     *
     * @return {void}
     */
    initInfiniteScrollObserver() {
        const loadMoreBtn = document.querySelector('.ais-InfiniteHits-loadMore');
        if (!loadMoreBtn || loadMoreBtn.dataset.observed) return;

        loadMoreBtn.dataset.observed = 'true';

        const observer = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting) {
                if (!loadMoreBtn.disabled) {
                    loadMoreBtn.click();
                }
            }
        }, {
            rootMargin: '200px'
        });

        observer.observe(loadMoreBtn);
    }

    resetStyles(){  
        (function () {
            const appBlock = document.querySelector(
                '.shopify-block.shopify-app-block[data-block-handle="instantsearch"]'
            );

            if (!appBlock) return;

            const styleEl = appBlock.querySelector('style');
            if (!styleEl) return;

            // Replace the selector
            styleEl.textContent = styleEl.textContent.replace(
                /\.ais-page\s+a,\s*\.ais-page\s+div,\s*\.ais-page\s+input,\s*\.ais-page\s+li,\s*\.ais-page\s+p,\s*\.ais-page\s+span,\s*\.ais-page\s+ul/g,
                '.no-render'
            );
        })();
    }

    /**
     * Escapes special regex characters in a string.
     *
     * @param {string} string - The string to escape.
     * @return {string} Escaped string.
     */
    escapeRegExp(string) {
        return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    }

    /**
     * Updates product card titles in Algolia search results using stored clean titles.
     *
     * @return {void} This method does not return a value.
     */
    updateProductCardTitles() {
        setTimeout(() => {
            if (!window.algoliaCleanTitles || Object.keys(window.algoliaCleanTitles).length === 0) {
                return;
            }

            const hitsContainer = document.querySelector('.ais-Hits-list, .ais-InfiniteHits-list');
            if (!hitsContainer) {
                return;
            }

            const hitItems = hitsContainer.querySelectorAll('.ais-Hits-item, .ais-InfiniteHits-item');

            if (hitItems.length === 0) {
                return;
            }

            hitItems.forEach((hitItem, index) => {
                if (hitItem.hasAttribute('data-title-updated')) {
                    return;
                }

                const titleElement = hitItem.querySelector('.ais-hit--title, .product-card-title');
                if (!titleElement) {
                    return;
                }

                let hitLink = titleElement.querySelector('a');
                if (!hitLink) {
                    hitLink = titleElement;
                }

                const productUrl = hitLink.getAttribute('href') || hitLink.closest('a')?.getAttribute('href');

                if (!productUrl || !productUrl.includes('/products/')) {
                    return;
                }

                const productHandle = productUrl.split('/products/').pop().split('?')[0].split('#')[0];
                const cleanTitle = window.algoliaCleanTitles[productHandle];

                if (cleanTitle) {
                    hitLink.textContent = cleanTitle;
                    if (hitLink.tagName === 'A') {
                        hitLink.setAttribute('title', cleanTitle);
                    }
                    hitItem.setAttribute('data-title-updated', 'true');
                }
            });
        }, 100);
    }

    /**
     * Adds promo blocks to the Algolia infinite hits list based on their data-position attribute.
     * 
     * @return {void}
     */
    addPromoBlocks() {
        // console.log('addPromoBlocks');
        const promoPool = document.querySelector('.section-promo-grid');
        const hitsList = document.querySelector('.ais-InfiniteHits-list');
        if (!hitsList || !promoPool) return;

        const promoBlocks = promoPool.querySelectorAll('.promotion-column');

        promoBlocks.forEach(promo => {
            const position = parseInt(promo.dataset.position);
            if (isNaN(position)) return;

            // Check if this specific promo is already in hitsList
            if (hitsList.querySelector(`.promotion-column[data-position="${position}"]`)) return;

            // Check if we have enough items to insert at this position
            if (hitsList.children.length >= position - 1) {
                const target = hitsList.children[position - 1];
                const clone = promo.cloneNode(true);
                if (target) {
                    hitsList.insertBefore(clone, target);
                } else {
                    hitsList.appendChild(clone);
                }
            }
        });
    }

    /**
     * Initializes a MutationObserver to watch for the hits list and its content.
     * This handles both the initial load and updates from infinite scroll.
     * 
     * @return {void}
     */
    initHitsObserver() {
        const _this = this;
        const target = document.querySelector('.ais-hits-container') || document.body;

        const observer = new MutationObserver((mutations) => {
            const hitsList = document.querySelector('.ais-InfiniteHits-list, .ais-Hits-list');
            if (hitsList && hitsList.querySelector('li')) {
                _this.updateProductCardTitles();
                _this.addPromoBlocks();
            }
        });

        observer.observe(target, {
            childList: true,
            subtree: true
        });

        // Initial check
        _this.addPromoBlocks();
    }

}

let algoliaCustomHooks = new AlgoliaCustomHooks();
algoliaCustomHooks.init();

// circle-a: product-level availability rollup (sold-out fix, push 2026-04)
// Bug context: storefront search was rendering "Sold out" whenever the search
// match resolved to a sold-out variant, even when other variants of the same
// product were in stock. Algolia indexes records at the variant level, but
// shoppers expect product-level availability on search cards.
//
// Rule: a product is "available" when ANY variant has inventory_quantity > 0.
//       "sold out" only when ALL variants are 0.
//       partial stock surfaces a "Some sizes available" pill.
window.CircleA = window.CircleA || {};
window.CircleA.computeAvailability = function (hit) {
  // hit may be either a variant-level record or a product-level rollup.
  // Variant-level: read inventory.variants[*].available_qty.
  // Product-level: read inventory.any_available + inventory.all_available.
  var inv = (hit && hit.inventory) || {};
  if (typeof inv.any_available === 'boolean') {
    return {
      available: inv.any_available,
      partial:   inv.any_available && inv.all_available === false,
      label: inv.any_available
        ? (inv.all_available ? 'In stock' : 'Some sizes available')
        : 'Sold out',
    };
  }
  var variants = (Array.isArray(inv.variants) ? inv.variants : []);
  if (!variants.length && Array.isArray(hit && hit.variants)) {
    variants = hit.variants;
  }
  var inStock = variants.filter(function (v) {
    return Number(v && (v.available_qty || v.inventory_quantity || 0)) > 0;
  });
  var anyAvailable = inStock.length > 0;
  var allAvailable = anyAvailable && inStock.length === variants.length;
  return {
    available: anyAvailable,
    partial: anyAvailable && !allAvailable,
    label: anyAvailable
      ? (allAvailable ? 'In stock' : 'Some sizes available')
      : 'Sold out',
  };
};

// Deep-link a sold-out variant search match to an in-stock variant of the
// same product when one exists. Called from the hit URL renderer below.
window.CircleA.resolveInStockVariantUrl = function (hit) {
  var baseUrl = hit && (hit.url || hit.handle ? '/products/' + hit.handle : '');
  if (!baseUrl) return baseUrl;
  var variants = (hit && hit.variants) || ((hit && hit.inventory && hit.inventory.variants) || []);
  var inStock = variants.filter(function (v) {
    return Number(v && (v.available_qty || v.inventory_quantity || 0)) > 0;
  });
  if (!inStock.length) return baseUrl;
  // Prefer a variant with a similar option1/option2 to the matched variant
  // when possible; fall back to the first in-stock.
  var pick = inStock[0];
  if (pick && pick.id) return baseUrl + '?variant=' + encodeURIComponent(pick.id);
  return baseUrl;
};
